import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-normal transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-200 disabled:pointer-events-none disabled:opacity-40 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-gray-900 text-gray-50 hover:bg-gray-800 hover:text-gray-100 active:bg-gray-900',
        destructive:
          'bg-red-500 text-gray-50 hover:bg-red-600 hover:text-gray-100 active:bg-red-500',
        outline:
          'border border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-500 active:bg-gray-100',
        secondary:
          'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-500 active:bg-gray-100',
        ghost: 'bg-transparent text-gray-700 hover:bg-gray-100 hover:text-gray-500 active:bg-gray-200',
        link: 'bg-transparent text-gray-900 hover:text-gray-600 underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-6 py-2',
        sm: 'h-8 px-4 text-xs',
        lg: 'h-12 px-8 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
